import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { DOCS_PATH } from '../../config/paths';
import { docsChartUrl } from '../../pages/docs/docsChartCatalog';
import { chainName, isTestnetMode, testnetFaucetUrl } from '../../constant/constant';

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.06 } },
};

const inView = {
    initial: 'hidden',
    whileInView: 'visible',
    viewport: { once: true, amount: 0.2 },
};

const CountUp = ({ to, suffix = '', duration = 1.4 }) => {
    const [value, setValue] = React.useState(0);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const start = performance.now();
                    const tick = (now) => {
                        const t = Math.min(1, (now - start) / (duration * 1000));
                        setValue(Math.floor(to * (1 - Math.pow(1 - t, 3))));
                        if (t < 1) requestAnimationFrame(tick);
                        else setValue(to);
                    };
                    requestAnimationFrame(tick);
                    observer.disconnect();
                });
            },
            { threshold: 0.35 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [to, duration]);

    return (
        <span ref={ref}>
            {value}
            {suffix}
        </span>
    );
};

export function JobsWorkspaceBlock({ onOpenJobs }) {
    const roles = [
        {
            id: 'company',
            icon: 'fa-solid fa-building-columns',
            title: 'Company',
            subtitle: 'Create tasks, fund escrow, approve work.',
            tags: ['Tasks', 'Escrow', 'Team'],
            gradient: 'from-teal-600/20 via-teal-500/5 to-transparent',
            ring: 'group-hover:shadow-[0_0_40px_-8px_rgba(20,184,166,0.55)]',
        },
        {
            id: 'employee',
            icon: 'fa-solid fa-id-badge',
            title: 'Employee',
            subtitle: 'Accept tasks, submit work, get paid.',
            tags: ['Assign', 'Submit', 'Payout'],
            gradient: 'from-emerald-600/15 via-teal-500/5 to-transparent',
            ring: 'group-hover:shadow-[0_0_40px_-8px_rgba(16,185,129,0.45)]',
        },
    ];

    return (
        <section
            id="jobs-portal"
            className="scroll-mt-20 border-t border-zinc-200/80 dark:border-zinc-800/60 sm:scroll-mt-24"
        >
            <div className="relative overflow-hidden">
                <div className="landing-section-grid pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-[0.2]" />
                <div className="pointer-events-none absolute -left-32 top-20 h-64 w-64 rounded-full bg-teal-400/15 blur-[100px] dark:bg-teal-600/10" />
                <div className="pointer-events-none absolute -right-32 bottom-0 h-64 w-64 rounded-full bg-emerald-400/10 blur-[100px]" />

                <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
                    <motion.div
                        variants={stagger}
                        {...inView}
                        className="mb-10 text-center sm:mb-12"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                        >
                            Workspace
                        </motion.span>
                        <motion.h2
                            variants={fadeUp}
                            className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                        >
                            Company &amp; Job
                        </motion.h2>
                        <motion.p
                            variants={fadeUp}
                            className="mx-auto mt-3 max-w-md text-sm text-zinc-600 dark:text-zinc-400 sm:text-base"
                        >
                            One portal for teams. Pick your role when you enter.
                        </motion.p>
                    </motion.div>

                    <motion.div
                        variants={stagger}
                        {...inView}
                        className="mx-auto max-w-4xl"
                    >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:gap-5">
                            {roles.map((role, i) => (
                                <React.Fragment key={role.id}>
                                    <motion.article
                                        variants={fadeUp}
                                        whileHover={{ y: -6 }}
                                        transition={{ type: 'spring', stiffness: 320, damping: 24 }}
                                        className={`landing-role-card group relative min-w-0 flex-1 overflow-hidden rounded-2xl border border-zinc-200/90 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900/90 sm:p-8 ${role.ring}`}
                                    >
                                        <div
                                            className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${role.gradient}`}
                                        />
                                        <div className="relative">
                                            <div className="mb-5 flex items-start justify-between">
                                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-teal-200/80 bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-900/20 dark:border-teal-700/50">
                                                    <i className={`${role.icon} text-lg`} aria-hidden />
                                                </div>
                                                <span className="font-mono text-[11px] font-medium text-zinc-400 dark:text-zinc-600">
                                                    0{i + 1}
                                                </span>
                                            </div>
                                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white">
                                                {role.title}
                                            </h3>
                                            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                                {role.subtitle}
                                            </p>
                                            <div className="mt-4 flex flex-wrap gap-2">
                                                {role.tags.map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="rounded-md border border-zinc-200/80 bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-400"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.article>

                                    {i === 0 ? (
                                        <motion.div
                                            variants={fadeUp}
                                            className="landing-escrow-bridge flex shrink-0 items-center justify-center self-center px-1 py-2 sm:w-[7.5rem] sm:px-0 sm:py-0 lg:w-[8.5rem]"
                                            aria-hidden
                                        >
                                            <span className="h-px flex-1 bg-gradient-to-r from-transparent via-teal-500/35 to-teal-500/55 dark:via-teal-500/45 sm:max-w-[2.75rem] sm:flex-none" />
                                            <span className="mx-2 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-teal-200/80 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-teal-800 shadow-sm dark:border-teal-800/60 dark:bg-zinc-900 dark:text-teal-300 sm:mx-2.5">
                                                <i className="fa-solid fa-link text-[9px]" aria-hidden />
                                                Escrow
                                            </span>
                                            <span className="h-px flex-1 bg-gradient-to-l from-transparent via-teal-500/35 to-teal-500/55 dark:via-teal-500/45 sm:max-w-[2.75rem] sm:flex-none" />
                                        </motion.div>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        {...inView}
                        className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
                    >
                        <button
                            type="button"
                            onClick={onOpenJobs}
                            className="group relative inline-flex min-h-[48px] items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 to-teal-500 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-teal-900/25 transition hover:shadow-teal-900/35 active:scale-[0.98]"
                        >
                            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                            <span className="relative">Open workspace</span>
                            <i className="fa-solid fa-arrow-right relative text-xs transition-transform group-hover:translate-x-0.5" />
                        </button>
                        <Link
                            to={`${DOCS_PATH}/jobs-portal`}
                            className="text-sm font-medium text-teal-700 underline-offset-4 hover:underline dark:text-teal-400"
                        >
                            Learn more in docs
                        </Link>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

export function HowItWorksBlock() {
    const steps = [
        {
            icon: 'fa-solid fa-wallet',
            title: 'Connect wallet',
            desc: `${chainName || 'Arc'} and USDC in your wallet.`,
        },
        {
            icon: 'fa-solid fa-layer-group',
            title: 'Choose a flow',
            desc: 'Direct pay, escrow, Get Paid, or Company & Job.',
        },
        {
            icon: 'fa-solid fa-circle-check',
            title: 'Sign in wallet',
            desc: 'Approve USDC and confirm. You stay in control.',
        },
    ];

    return (
        <section
            id="how-it-works"
            className="scroll-mt-20 border-t border-zinc-200/80 bg-zinc-100/50 dark:border-zinc-800/60 dark:bg-zinc-950/50 sm:scroll-mt-24"
        >
            <div className="relative overflow-hidden">
                <div className="landing-section-grid pointer-events-none absolute inset-0 opacity-30 dark:opacity-15" />

                <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
                    <motion.div variants={stagger} {...inView} className="mb-12 text-center sm:mb-14">
                        <motion.span
                            variants={fadeUp}
                            className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                        >
                            Get started
                        </motion.span>
                        <motion.h2
                            variants={fadeUp}
                            className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                        >
                            How it works
                        </motion.h2>
                    </motion.div>

                    <motion.ol
                        variants={stagger}
                        {...inView}
                        className="relative mx-auto grid max-w-4xl gap-6 sm:grid-cols-3 sm:gap-5"
                    >
                        <motion.div
                            aria-hidden
                            className="pointer-events-none absolute left-[14%] right-[14%] top-[3.35rem] hidden h-px origin-left bg-gradient-to-r from-transparent via-teal-500/70 to-transparent sm:block"
                            initial={{ scaleX: 0, opacity: 0 }}
                            whileInView={{ scaleX: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        />

                        {steps.map((step, index) => (
                            <motion.li
                                key={step.title}
                                variants={fadeUp}
                                className="landing-step-card relative list-none rounded-2xl border border-zinc-200/90 bg-white/80 p-6 text-center shadow-sm backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-900/80"
                            >
                                <motion.div
                                    className="relative z-[1] mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-900/20"
                                    whileHover={{ scale: 1.06, rotate: index === 1 ? 0 : -2 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                                >
                                    <i className={`${step.icon} text-xl`} aria-hidden />
                                    <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-zinc-900 text-[9px] font-bold text-white dark:border-zinc-900">
                                        {index + 1}
                                    </span>
                                </motion.div>
                                <h3 className="text-base font-bold text-zinc-900 dark:text-white sm:text-lg">
                                    {step.title}
                                </h3>
                                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-400">{step.desc}</p>
                            </motion.li>
                        ))}
                    </motion.ol>
                </div>
            </div>
        </section>
    );
}

export function TrustStripBlock() {
    const networkLabel = chainName || 'Arc';
    const items = [
        {
            icon: 'fa-solid fa-link',
            label: networkLabel,
            hint: 'Built on Arc',
        },
        {
            icon: 'fa-solid fa-circle-dollar-to-slot',
            label: 'USDC-native',
            hint: 'Stablecoin settlements',
        },
        {
            icon: 'fa-solid fa-wallet',
            label: 'Non-custodial',
            hint: 'You sign every tx',
        },
        {
            icon: 'fa-brands fa-ethereum',
            label: 'Wallet-ready',
            hint: 'MetaMask, Rainbow, WC',
        },
    ];

    return (
        <section
            id="networks"
            className="border-y border-zinc-200/90 bg-white/80 dark:border-zinc-800/80 dark:bg-zinc-950/50"
        >
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-4 px-4 py-5 sm:gap-x-12 sm:px-6 lg:px-8">
                {items.map((item) => (
                    <div
                        key={item.label}
                        className="flex items-center gap-3"
                    >
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-teal-200/80 bg-teal-50 text-teal-700 dark:border-teal-800/60 dark:bg-teal-950/40 dark:text-teal-300">
                            <i className={`${item.icon} text-sm`} aria-hidden />
                        </span>
                        <div>
                            <p className="text-sm font-bold text-zinc-900 dark:text-white">
                                {item.label}
                            </p>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-500">{item.hint}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

export function UsdcFlowBlock() {
    const steps = [
        {
            icon: 'fa-solid fa-wallet',
            title: 'Connect wallet',
            desc: 'Link MetaMask, Rainbow, or WalletConnect.',
        },
        {
            icon: 'fa-solid fa-circle-check',
            title: 'Approve USDC',
            desc: 'Approve only the amount needed for each task or payment.',
        },
        {
            icon: 'fa-solid fa-lock',
            title: 'Lock in escrow',
            desc: 'Funds stay on-chain until terms are met.',
        },
        {
            icon: 'fa-solid fa-paper-plane',
            title: 'Release payout',
            desc: 'Approve work and USDC moves to the receiver.',
        },
    ];

    return (
        <section className="border-t border-zinc-200/80 bg-zinc-50/80 dark:border-zinc-800/60 dark:bg-zinc-950/30">
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
                <motion.div variants={stagger} {...inView} className="mb-10 text-center sm:mb-12">
                    <motion.span
                        variants={fadeUp}
                        className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                    >
                        USDC flow
                    </motion.span>
                    <motion.h2
                        variants={fadeUp}
                        className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                    >
                        How USDC moves on CronPay
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        className="mx-auto mt-3 max-w-lg text-sm text-zinc-600 dark:text-zinc-400 sm:text-base"
                    >
                        Transparent steps from your wallet to on-chain settlement.
                    </motion.p>
                </motion.div>

                <motion.ol
                    variants={stagger}
                    {...inView}
                    className="relative grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute left-[10%] right-[10%] top-7 hidden h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent lg:block"
                    />
                    {steps.map((step, index) => (
                        <motion.li
                            key={step.title}
                            variants={fadeUp}
                            className="landing-flow-step relative list-none rounded-2xl border border-zinc-200/90 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900/80"
                        >
                            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-md shadow-teal-900/20">
                                <i className={`${step.icon} text-sm`} aria-hidden />
                            </div>
                            <span className="mb-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-bold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                {index + 1}
                            </span>
                            <h3 className="text-base font-bold text-zinc-900 dark:text-white">
                                {step.title}
                            </h3>
                            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-400">{step.desc}</p>
                        </motion.li>
                    ))}
                </motion.ol>
            </div>
        </section>
    );
}

export function TrustPillarsBlock() {
    const pillars = [
        {
            icon: 'fa-solid fa-key',
            title: 'Your wallet, your funds',
            desc: 'CronPay never holds USDC. You approve and sign every transfer.',
        },
        {
            icon: 'fa-solid fa-cube',
            title: 'On-chain escrow',
            desc: 'Tracked payments and task payouts lock USDC in smart contracts.',
        },
        {
            icon: 'fa-solid fa-scale-balanced',
            title: 'Built-in disputes',
            desc: 'Structured resolution when work or milestones need review.',
        },
        {
            icon: 'fa-solid fa-shield-halved',
            title: 'Trust & safety',
            desc: 'Clear rules for senders, receivers, and teams on testnet.',
            link: `${DOCS_PATH}/trust-and-safety`,
        },
    ];

    return (
        <section className="border-t border-zinc-200/80 dark:border-zinc-800/60">
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
                <motion.div variants={stagger} {...inView} className="mb-10 text-center sm:mb-12">
                    <motion.span
                        variants={fadeUp}
                        className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                    >
                        Web3 by design
                    </motion.span>
                    <motion.h2
                        variants={fadeUp}
                        className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                    >
                        Why teams choose CronPay
                    </motion.h2>
                </motion.div>

                <motion.div
                    variants={stagger}
                    {...inView}
                    className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
                >
                    {pillars.map((p) => (
                        <motion.div
                            key={p.title}
                            variants={fadeUp}
                            className="rounded-2xl border border-zinc-200/90 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900/80"
                        >
                            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-teal-200/80 bg-teal-50 text-teal-700 dark:border-teal-800/60 dark:bg-teal-950/40 dark:text-teal-300">
                                <i className={`${p.icon} text-sm`} aria-hidden />
                            </div>
                            <h3 className="text-base font-bold text-zinc-900 dark:text-white">{p.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                {p.desc}
                            </p>
                            {p.link ? (
                                <Link
                                    to={p.link}
                                    className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:underline dark:text-teal-400"
                                >
                                    Read more
                                    <i className="fa-solid fa-arrow-right text-[10px]" />
                                </Link>
                            ) : null}
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

export function UseCasesBlock({ onLaunch, onOpenJobs }) {
    const cases = [
        {
            icon: 'fa-solid fa-user-tie',
            title: 'Freelancers',
            desc: 'Send payment requests and get paid in USDC when work is approved.',
            cta: 'Get paid',
            action: onLaunch,
        },
        {
            icon: 'fa-solid fa-handshake',
            title: 'Clients & businesses',
            desc: 'Lock USDC in tracked escrow until milestones or deliverables are met.',
            cta: 'Pay with escrow',
            action: onLaunch,
        },
        {
            icon: 'fa-solid fa-users',
            title: 'Teams',
            desc: 'Run tasks, approvals, and payouts in the Company & Job workspace.',
            cta: 'Open workspace',
            action: onOpenJobs,
        },
    ];

    return (
        <section
            id="use-cases"
            className="scroll-mt-20 border-t border-zinc-200/80 bg-zinc-50/50 dark:border-zinc-800/60 dark:bg-zinc-950/20 sm:scroll-mt-24"
        >
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
                <motion.div variants={stagger} {...inView} className="mb-10 text-center sm:mb-12">
                    <motion.span
                        variants={fadeUp}
                        className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                    >
                        Use cases
                    </motion.span>
                    <motion.h2
                        variants={fadeUp}
                        className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                    >
                        Built for real payment workflows
                    </motion.h2>
                </motion.div>

                <motion.div
                    variants={stagger}
                    {...inView}
                    className="grid gap-6 md:grid-cols-3"
                >
                    {cases.map((c) => (
                        <motion.article
                            key={c.title}
                            variants={fadeUp}
                            whileHover={{ y: -4 }}
                            className="flex flex-col rounded-2xl border border-zinc-200/90 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900/80"
                        >
                            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-900/15">
                                <i className={`${c.icon} text-lg`} aria-hidden />
                            </div>
                            <h3 className="text-lg font-bold text-zinc-900 dark:text-white">{c.title}</h3>
                            <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                {c.desc}
                            </p>
                            <button
                                type="button"
                                onClick={c.action}
                                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal-700 transition hover:text-teal-600 dark:text-teal-400"
                            >
                                {c.cta}
                                <i className="fa-solid fa-arrow-right text-xs" />
                            </button>
                        </motion.article>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

export function ProtocolVisualBlock() {
    return (
        <section className="border-t border-zinc-200/80 dark:border-zinc-800/60">
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
                <motion.div
                    variants={stagger}
                    {...inView}
                    className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14"
                >
                    <motion.div variants={fadeUp}>
                        <span className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400">
                            Protocol map
                        </span>
                        <h2 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl">
                            One hub for USDC on Arc
                        </h2>
                        <p className="mt-4 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 sm:text-base">
                            Direct pay, tracked escrow, payment requests, team tasks, and disputes
                            connect through CronPay with your wallet at the center.
                        </p>
                        <Link
                            to={`${DOCS_PATH}/visual-guide`}
                            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-teal-900 transition hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-950/60"
                        >
                            Explore all diagrams
                            <i className="fa-solid fa-arrow-right text-xs" />
                        </Link>
                    </motion.div>
                    <motion.div
                        variants={fadeUp}
                        className="overflow-hidden rounded-2xl border border-zinc-200/90 bg-white p-2 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/80"
                    >
                        <img
                            src={`${process.env.PUBLIC_URL || ''}${docsChartUrl('how-cronpay-works')}`}
                            alt="CronPay protocol overview: wallet, direct pay, tracked escrow, payment requests, jobs workspace, and disputes"
                            className="w-full rounded-xl"
                            loading="lazy"
                        />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export function TestnetOnboardingBlock({ onLaunch }) {
    if (!isTestnetMode) return null;

    const steps = [
        {
            n: '1',
            title: 'Launch app',
            desc: 'Open the dashboard and connect your wallet there.',
            launch: true,
        },
        {
            n: '2',
            title: `Switch to ${chainName || 'Arc'}`,
            desc: 'Approve the network switch in your wallet app.',
        },
        {
            n: '3',
            title: 'Get test USDC',
            desc: 'Use the Circle faucet for Arc Testnet.',
            faucet: true,
        },
        {
            n: '4',
            title: 'Send your first payment',
            desc: 'Try direct pay or tracked escrow from the dashboard.',
        },
    ];

    return (
        <section
            id="try-testnet"
            className="scroll-mt-20 border-t border-teal-200/60 bg-gradient-to-b from-teal-50/80 to-white dark:border-teal-900/40 dark:from-teal-950/30 dark:to-zinc-950 sm:scroll-mt-24"
        >
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
                <motion.div variants={stagger} {...inView} className="mb-10 text-center sm:mb-12">
                    <motion.span
                        variants={fadeUp}
                        className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                    >
                        Try it now
                    </motion.span>
                    <motion.h2
                        variants={fadeUp}
                        className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                    >
                        Test CronPay in minutes
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        className="mx-auto mt-3 max-w-md text-sm text-zinc-600 dark:text-zinc-400"
                    >
                        Live on Arc Testnet with USDC. No real money required.
                    </motion.p>
                </motion.div>

                <motion.ol
                    variants={stagger}
                    {...inView}
                    className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
                >
                    {steps.map((step) => (
                        <motion.li
                            key={step.n}
                            variants={fadeUp}
                            className="list-none rounded-2xl border border-teal-200/80 bg-white p-5 dark:border-teal-900/50 dark:bg-zinc-900/80"
                        >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white">
                                {step.n}
                            </span>
                            <h3 className="mt-3 text-base font-bold text-zinc-900 dark:text-white">
                                {step.title}
                            </h3>
                            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-400">{step.desc}</p>
                            {step.faucet ? (
                                <a
                                    href={testnetFaucetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:underline dark:text-teal-400"
                                >
                                    Open faucet
                                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                                </a>
                            ) : null}
                            {step.launch ? (
                                <button
                                    type="button"
                                    onClick={onLaunch}
                                    className="mt-3 text-sm font-semibold text-teal-700 hover:underline dark:text-teal-400"
                                >
                                    Launch dashboard
                                </button>
                            ) : null}
                        </motion.li>
                    ))}
                </motion.ol>
            </div>
        </section>
    );
}

export function TrustMetricsBlock() {
    const metrics = [
        {
            icon: 'fa-solid fa-link',
            value: 100,
            suffix: '%',
            label: 'On-chain settled',
            hint: 'Transparent settlement',
            accent: 'from-teal-500/15 to-transparent',
        },
        {
            icon: 'fa-solid fa-clock',
            value: 24,
            suffix: '/7',
            label: 'Always on',
            hint: 'Pay when you need to',
            accent: 'from-cyan-500/10 to-transparent',
        },
        {
            icon: 'fa-solid fa-receipt',
            value: 0,
            suffix: '',
            label: 'Hidden fees',
            hint: 'No platform markup',
            accent: 'from-emerald-500/10 to-transparent',
            display: 'Zero',
        },
    ];

    return (
        <section className="border-t border-zinc-200/80 dark:border-zinc-800/60">
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
                <motion.div
                    variants={stagger}
                    {...inView}
                    className="grid gap-4 sm:grid-cols-3 sm:gap-5"
                >
                    {metrics.map((m) => (
                        <motion.div
                            key={m.label}
                            variants={fadeUp}
                            whileHover={{ y: -4 }}
                            className="landing-metric-card group relative overflow-hidden rounded-2xl border border-zinc-200/90 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900/80"
                        >
                            <div
                                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${m.accent} opacity-80 transition-opacity group-hover:opacity-100`}
                            />
                            <div className="relative">
                                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-200/80 bg-zinc-50 text-teal-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-teal-400">
                                    <i className={`${m.icon} text-sm`} aria-hidden />
                                </div>
                                <p className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl">
                                    {m.display ? (
                                        m.display
                                    ) : (
                                        <CountUp to={m.value} suffix={m.suffix} />
                                    )}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                                    {m.label}
                                </p>
                                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-500">{m.hint}</p>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
